//our root app component
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatDatepicker } from '@angular/material/datepicker';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { AlertService } from '../../../core';
import { ChangesDisplayService } from '../../changes/changes-display.service';
import { AlertDialogComponent } from '../../dialogs';
import { IChangedFieldDetail } from '../../pay-categories/pay-categories.type';
import { PayScalesService } from '../pay-scales.service';
import { IPayScaleGlobalSettings, IPayScaleGlobalSettingsHistory } from '../pay-scales.types';
import { BaseComponent } from 'app/pilotpay/base.component';

export const MONTH_MODE_FORMATS: Object = {
	parse: {
		dateInput: 'MMMM-YYYY'
	},
	display: {
		dateInput: 'MMMM YYYY',
		monthYearLabel: 'MMM YYYY',
		dateA11yLabel: 'LL',
		monthYearA11yLabel: 'MMMM YYYY'
	}
};

@Component({
	selector: 'app-pilotpay-pay-scales-settings',
	templateUrl: './pay-scales-settings.component.html',
	providers: [{ provide: MAT_DATE_FORMATS, useValue: MONTH_MODE_FORMATS }]
})
export class PayScalesSettingsComponent extends BaseComponent implements OnInit {
	constructor(
		protected alertService: AlertService,
		private router: Router,
		private dialog: MatDialog,
		private payScalesService: PayScalesService,
		private changeDisplayService: ChangesDisplayService,
		private fb: FormBuilder
	) {
		super(alertService);

		this.dialogConfig = new MatDialogConfig();
		this.payScaleGlobalSettingHistory = [];
	}

	public futureChangesToggle: boolean = false;
	public historyToggle: boolean = false;
	public alertDialogRef: MatDialogRef<AlertDialogComponent>;
	public payScaleGlobalSettingHistory: IChangedFieldDetail[];
	public payScaleGlobalSettingChanges: IChangedFieldDetail[];
	public currentMonth: moment.Moment = moment.utc().startOf('month');
	public dialogConfig: MatDialogConfig;
	public form: FormGroup;

	public ngOnInit(): void {
		this.form = this.fb.group({
			perDiemRate: [null, Validators.required],
			loadPayRate: [null, Validators.required],
			apBonusPercentage: [null, Validators.required],
			phoneStipendAmount: [null, Validators.required],
			instructFlatRate: [null, Validators.required],
			effectiveDate: [this.currentMonth, Validators.required]
		});

		this.getPayScaleSetting();
		this.getPayScaleGlobalSettingChanges();
		this.getPayScaleGlobalSettingsHistory();
	}

	public chosenMonthHandler(normalizedMonth: moment.Moment, datepicker: MatDatepicker<moment.Moment>): void {
		this.form.get('effectiveDate').patchValue(normalizedMonth.startOf('day'));
		datepicker.close();
	}

	/**
	 *
	 * createPayScaleSetting
	 */
	public createPayScaleSetting(): void {
		this.form.value.effectiveDate = moment.utc(this.form.value.effectiveDate).format('YYYY-MM-DDTHH:mm:ss');
		this.payScalesService.createPayScaleSetting(this.form.value).subscribe(
			(DATA: IPayScaleGlobalSettings) => {
				this.router.navigate(['/pilotpay/pay-scales']);
			},
			(error: HttpErrorResponse) => {
				this.handleError(error);
			}
		);
	}

	/**
	 * getPayScaleSetting
	 *
	 */
	public getPayScaleSetting(): void {
		this.payScalesService.getPayScaleSetting().subscribe(
			(DATA: IPayScaleGlobalSettings) => {
				if (DATA) {
					const { perDiemRate, loadPayRate, apBonusPercentage, phoneStipendAmount, instructFlatRate } = DATA;

					this.form.patchValue({
						perDiemRate,
						loadPayRate,
						apBonusPercentage,
						phoneStipendAmount,
						instructFlatRate
					});
				}
			},
			(error: HttpErrorResponse) => {
				console.error(error);
			}
		);
	}

	/**
	 * getPayScaleSettingFutureChanges
	 *
	 */
	public getPayScaleGlobalSettingChanges(): void {
		this.payScaleGlobalSettingChanges = [];
		this.payScalesService.getPayScaleGlobalSettingChanges().subscribe(
			(DATA: IPayScaleGlobalSettingsHistory[]) => {
				if (DATA) {
					const history: IChangedFieldDetail[] = this.changeDisplayService.convertPayCategoryHistoryToChangedFieldDetail(
						DATA
					);
					this.payScaleGlobalSettingChanges = [...this.payScaleGlobalSettingChanges, ...history];
				}
			},
			(error: HttpErrorResponse) => {
				console.error(error);
			}
		);
	}

	/**
	 * getPayScaleSettingsHistory
	 *
	 */
	public getPayScaleGlobalSettingsHistory(): void {
		this.payScaleGlobalSettingHistory = [];
		this.payScalesService.getPayScaleSettingsHistory().subscribe(
			(DATA: IPayScaleGlobalSettingsHistory[]) => {
				if (DATA) {
					const history: IChangedFieldDetail[] = this.changeDisplayService.convertPayScaleSettingHistoryToChangedFieldDetail(
						DATA
					);
					this.payScaleGlobalSettingHistory = [...this.payScaleGlobalSettingHistory, ...history];
				}
			},
			(error: HttpErrorResponse) => {
				console.error(error);
			}
		);
	}

	/**
	 * deletePayScaleGlobalSettingScheduledChanges
	 */
	public deletePayScaleGlobalSettingScheduledChanges(): void {
		this.dialogConfig.disableClose = false;
		this.dialogConfig.autoFocus = true;
		this.dialogConfig.data = {
			modalType: 'deleteFutureChanges',
			name: 'Future Changes'
		};
		if (this.dialog) {
			this.alertDialogRef = this.dialog.open(AlertDialogComponent, this.dialogConfig);
		}
		if (this.alertDialogRef) {
			this.alertDialogRef.afterClosed().subscribe((isTrue: boolean) => {
				this.alertDialogRef = null;
				if (isTrue) {
					this.payScalesService.deletePayScaleGlobalSettingScheduledChanges().subscribe(
						() => {
							this.payScaleGlobalSettingChanges = [];
						},
						(error: HttpErrorResponse) => {
							console.error(error);
							this.handleError(error);
						}
					);
				}
			});
		}
	}
}
